import { signal } from '@angular/core';
import {
  SILENCE_AUTO_STOP_MS,
  SILENCE_RMS_THRESHOLD,
  WAVEFORM_BAR_BIN_RANGES,
  WAVEFORM_BAR_COUNT,
  WAVEFORM_FFT_SIZE,
  WAVEFORM_GAIN,
  WAVEFORM_MAX_BAR_HEIGHT_PX,
  WAVEFORM_MIN_BAR_HEIGHT_PX,
} from './voice.constants';

/**
 * Drives the 4-bar voice waveform indicator and the silence-detection autostop
 * from a single AnalyserNode bifurcated off the recording sourceNode.
 *
 * - `setup(audioContext, sourceNode)` wires the analyser and starts the RAF loop.
 * - `teardown()` cancels the loop, disconnects the analyser, resets bars to min.
 * - `barHeights` is the signal the host component renders.
 * - `onSilenceTimeout` fires once when the user has been silent for
 *   SILENCE_AUTO_STOP_MS continuous ms; the host typically responds by stopping
 *   the recording.
 *
 * Plain class (no Angular DI) so it can be unit-tested without TestBed and used
 * from any component lifecycle without a host directive.
 */
export class WaveformAnalyzer {
  private analyserNode?: AnalyserNode;
  private analyserData?: Uint8Array<ArrayBuffer>;
  private rafHandle: number | null = null;
  private silenceStartTs: number | null = null;

  readonly barHeights = signal<number[]>(new Array(WAVEFORM_BAR_COUNT).fill(WAVEFORM_MIN_BAR_HEIGHT_PX));

  constructor(private readonly onSilenceTimeout: () => void) {}

  setup(audioContext: AudioContext, sourceNode: MediaStreamAudioSourceNode): void {
    this.analyserNode = audioContext.createAnalyser();
    this.analyserNode.fftSize = WAVEFORM_FFT_SIZE;
    this.analyserNode.smoothingTimeConstant = 0.4;
    this.analyserData = new Uint8Array(new ArrayBuffer(this.analyserNode.frequencyBinCount));
    sourceNode.connect(this.analyserNode);
    this.silenceStartTs = null;
    this.startLoop();
  }

  teardown(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    if (this.analyserNode) {
      try {
        this.analyserNode.disconnect();
      } catch {
        // already disconnected
      }
      this.analyserNode = undefined;
    }
    this.analyserData = undefined;
    this.silenceStartTs = null;
    this.barHeights.set(new Array(WAVEFORM_BAR_COUNT).fill(WAVEFORM_MIN_BAR_HEIGHT_PX));
  }

  private startLoop(): void {
    if (this.rafHandle !== null) {
      return;
    }
    const tick = (): void => {
      if (!this.analyserNode || !this.analyserData) {
        this.rafHandle = null;
        return;
      }
      this.tick();
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  /**
   * Single per-frame pass: read FFT once, compute voice-band average once, use
   * it for BOTH the bar gate and the silence detector. Exposed via a wrapper to
   * keep test access ergonomic.
   */
  private tick(): void {
    if (!this.analyserData) {
      return;
    }
    if (this.analyserNode) {
      this.analyserNode.getByteFrequencyData(this.analyserData);
    }

    const bins = this.analyserData.length;
    const voiceStart = Math.min(WAVEFORM_BAR_BIN_RANGES[0][0], bins);
    const voiceEnd = Math.min(WAVEFORM_BAR_BIN_RANGES[WAVEFORM_BAR_COUNT - 1][1], bins);
    let voiceTotal = 0;
    for (let j = voiceStart; j < voiceEnd; j++) {
      voiceTotal += this.analyserData[j];
    }
    const voiceAvg = voiceEnd > voiceStart ? voiceTotal / (voiceEnd - voiceStart) : 0;

    this.updateBarHeights(voiceAvg, bins);
    this.updateSilence(voiceAvg);
  }

  private updateBarHeights(voiceAvg: number, bins: number): void {
    if (voiceAvg < SILENCE_RMS_THRESHOLD) {
      this.barHeights.set(new Array(WAVEFORM_BAR_COUNT).fill(WAVEFORM_MIN_BAR_HEIGHT_PX));
      return;
    }
    const data = this.analyserData;
    if (!data) {
      return;
    }
    const range = WAVEFORM_MAX_BAR_HEIGHT_PX - WAVEFORM_MIN_BAR_HEIGHT_PX;
    const heights = new Array<number>(WAVEFORM_BAR_COUNT);
    for (let i = 0; i < WAVEFORM_BAR_COUNT; i++) {
      const [rangeStart, rangeEnd] = WAVEFORM_BAR_BIN_RANGES[i];
      const start = Math.min(rangeStart, bins);
      const end = Math.min(rangeEnd, bins);
      if (end <= start) {
        heights[i] = WAVEFORM_MIN_BAR_HEIGHT_PX;
        continue;
      }
      let sum = 0;
      for (let j = start; j < end; j++) {
        sum += data[j];
      }
      const avg = sum / (end - start);
      const normalized = Math.min(1, (avg / 255) * WAVEFORM_GAIN);
      heights[i] = WAVEFORM_MIN_BAR_HEIGHT_PX + Math.round(normalized * range);
    }
    this.barHeights.set(heights);
  }

  private updateSilence(voiceAvg: number): void {
    const now = performance.now();
    if (voiceAvg < SILENCE_RMS_THRESHOLD) {
      if (this.silenceStartTs === null) {
        this.silenceStartTs = now;
      } else if (now - this.silenceStartTs >= SILENCE_AUTO_STOP_MS) {
        this.silenceStartTs = null;
        this.onSilenceTimeout();
      }
    } else {
      this.silenceStartTs = null;
    }
  }
}
